// Nova OS em sequência da anterior (#88): o operador lança várias comandas
// seguidas do mesmo hotel, de um bloco de numeração contínua. A sugestão é o
// que a Ficha da Nova OS preenche quando a pessoa escolhe "Em Sequência".
// Pura — a escolha e qual é a OS anterior ficam na tela.

import { diaSemanaISO, somarDias, type DataISO } from "./datas.ts";
import type { Id } from "./ids.ts";
import { paradasDeColeta } from "./paradas.ts";
import type { Snapshot } from "./snapshot.ts";

export interface SugestaoSequencia {
  /** Número da OS anterior, para o aviso "Em sequência da OS nnn". */
  anteriorOs: number;
  clienteId: Id;
  /** Parada de coleta; `null` = Coleta Avulsa. */
  paradaId: Id | null;
  data: DataISO;
  hora: string;
  /** OS anterior + 1. */
  os: number;
  /** O número já existe: a Ficha só avisa — o "Lançar Coleta" recusa repetido. */
  osOcupada: boolean;
}

/**
 * A sugestão em sequência de `anteriorId` (a última OS lançada pelo
 * operador). `null` sem OS anterior, ou se ela não existe mais ou foi
 * cancelada, ou se o Cliente dela foi desativado — sem anterior não há
 * sequência (decisão do dono). Regra:
 * - Cliente: o da anterior;
 * - Parada: a de coleta da anterior; se ela foi avulsa, a primeira Parada de
 *   coleta do Cliente; sem nenhuma, Coleta Avulsa;
 * - data: o primeiro dia depois da coleta da anterior que é dia da Parada
 *   (sem Parada, o dia seguinte); hora: a da Parada (sem Parada, a da anterior);
 * - OS: anterior + 1.
 */
export function sugestaoSequencia(snapshot: Snapshot, anteriorId: Id | null): SugestaoSequencia | null {
  const anterior = anteriorId == null ? undefined : snapshot.lotes.find((l) => l.id === anteriorId);
  if (!anterior || anterior.canceladoEm) return null;
  // Cliente desativado não aparece na Nova OS: sem ele, não há sequência
  if (!snapshot.clientes.find((c) => c.id === anterior.clienteId)?.ativo) return null;
  const deColeta = paradasDeColeta(snapshot, anterior.clienteId);
  const parada = deColeta.find((p) => p.id === anterior.paradaId) ?? deColeta[0] ?? null;

  let data = somarDias(anterior.coletaData, 1);
  if (parada && parada.diasSemana.length > 0) {
    for (let i = 0; i < 7 && !parada.diasSemana.includes(diaSemanaISO(data)); i++) data = somarDias(data, 1);
  }
  const os = anterior.os + 1;
  return {
    anteriorOs: anterior.os,
    clienteId: anterior.clienteId,
    paradaId: parada?.id ?? null,
    data,
    hora: parada?.horario ?? anterior.coletaHora,
    os,
    osOcupada: snapshot.lotes.some((l) => l.os === os),
  };
}
